import React, { useEffect, useRef, useState } from "react";
import { useTheme } from "styled-components";
import { FiChevronLeft } from "react-icons/fi";
import { Meter, TopBar, tokens } from "../../activity-logs/styles";
import { colorFor, initials, relativeTime } from "../../activity-logs/labels";
import { AvatarBox, BackLink, MoneyText } from "../styles";
import { formatCurrency, currencyParts } from "../dashboardData";

export function MoneyValue({ value }) {
  const { currency, integer, decimal } = currencyParts(value);
  return (
    <MoneyText title={formatCurrency(value)}>
      <span className="cur">{currency}</span>{integer}<span className="dec">{decimal}</span>
    </MoneyText>
  );
}

export function SupplierAvatar({ name, size = 36 }) {
  return <AvatarBox $size={size} $color={colorFor(name)} title={name}>{initials(name)}</AvatarBox>;
}

export function BackButton({ onClick, children }) {
  return <BackLink type="button" onClick={onClick}><FiChevronLeft /> {children}</BackLink>;
}

// Borda da barra superior ao rolar. Telas de detalhe abrem sempre do topo.
export function useScrolledTopBar({ scrollToTop = false, deps = [] } = {}) {
  const sentinel = useRef(null);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const el = sentinel.current;
    if (!el) return undefined;
    if (scrollToTop) el.scrollIntoView({ block: "start" });
    const io = new IntersectionObserver(([e]) => setScrolled(!e.isIntersecting));
    io.observe(el);
    return () => io.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return { sentinel, scrolled };
}

/**
 * Barra superior com a borda de rolagem. O estado "rolou" fica só aqui dentro:
 * no componente da tela, cruzar o topo re-renderizava a página inteira
 * (gráficos e cards), com travadas de 90 a 140 ms ao rolar.
 */
export function ScrolledTopBar({ children, scrollToTop = false, deps = [] }) {
  const { sentinel, scrolled } = useScrolledTopBar({ scrollToTop, deps });
  return (
    <>
      <div ref={sentinel} style={{ height: 1 }} />
      <TopBar $scrolled={scrolled}>{children}</TopBar>
    </>
  );
}

// "atualizado há…" que se atualiza sozinho, sem re-renderizar a tela a cada 30s
export function UpdatedAgo({ at }) {
  const [, tick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => tick((x) => x + 1), 30000);
    return () => clearInterval(t);
  }, []);
  if (!at) return null;
  return <span className="update-tag">atualizado {relativeTime(at)}</span>;
}

// Linha de ranking: rótulo e valor, com barra fina proporcional ao maior da lista
export function RankRows({ rows, color, max, format = formatCurrency, labelOf = (r) => r.key, onSelect, limit = 6 }) {
  const theme = useTheme();
  const top = max || Math.max(1, ...rows.map((r) => r.total));
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 11 }}>
      {rows.slice(0, limit).map((r) => (
        <div
          key={r.key}
          role={onSelect ? "button" : undefined}
          tabIndex={onSelect ? 0 : undefined}
          style={{ cursor: onSelect ? "pointer" : "default" }}
          onClick={onSelect ? () => onSelect(r) : undefined}
          onKeyDown={onSelect ? (e) => { if (e.key === "Enter") onSelect(r); } : undefined}
        >
          <div style={{ display: "flex", justifyContent: "space-between", gap: 10, fontSize: "0.84em" }}>
            <span style={{ fontWeight: 500, minWidth: 0, overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }} title={labelOf(r)}>{labelOf(r)}</span>
            <span style={{ color: tokens.label2(theme), whiteSpace: "nowrap" }}>{format(r.total)}</span>
          </div>
          <Meter $color={color}><div style={{ width: `${(r.total / top) * 100}%` }} /></Meter>
        </div>
      ))}
    </div>
  );
}
